import {BellIcon, SearchIcon, XCircleFillIcon, LogoGithubIcon, MarkGithubIcon} from '@primer/octicons-react'
import {Box, Octicon, Text, TextInput, useColorSchemeVar, Link} from '@primer/react'
import React, {type FormEvent, type KeyboardEventHandler, useState, useEffect} from 'react'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import PrefixesDialog from '../components/PrefixesDialog'
import {useNavigateToQuery} from '../hooks/use-navigate-to-query'
import type {HomePayload} from '../types/blackbird-types'

export default function Home() {
  const [value, setValue] = useState('')
  const payload = useRoutePayload<HomePayload | null>()
  const returnFocusRef = React.useRef(null)
  const [dialogIsOpen, setDialogIsOpen] = useState(false)
  const navigateToQuery = useNavigateToQuery()

  useHideHeader()

  const handleKeyPress: KeyboardEventHandler = e => {
    // TODO: somehow exempt react apps from this?
    // eslint-disable-next-line no-restricted-syntax -- manual shortcut logic is idiomatic in React
    if (value && e.key === 'Enter') {
      navigateToQuery(value)
    }
  }

  const lightIllustrationPath = '/images/modules/search/home-desktop-light.png'
  const lightIllustrationPath2x = '/images/modules/search/home-desktop-light2x.png'
  const lightIllustrationPathWebP = '/images/modules/search/home-desktop-light.webp'
  const lightIllustrationPathWebP2x = '/images/modules/search/home-desktop-light2x.webp'

  const darkIllustrationPath = '/images/modules/search/home-desktop-dark.png'
  const darkIllustrationPath2x = '/images/modules/search/home-desktop-dark2x.png'
  const darkIllustrationPathWebP = '/images/modules/search/home-desktop-dark.webp'
  const darkIllustrationPathWebP2x = '/images/modules/search/home-desktop-dark2x.webp'

  const lightIllustrationPathMobile = '/images/modules/search/home-mobile-light.png'
  const lightIllustrationPathMobile2x = '/images/modules/search/home-mobile-light2x.png'
  const lightIllustrationPathMobileWebP = '/images/modules/search/home-mobile-light.webp'
  const lightIllustrationPathMobileWebP2x = '/images/modules/search/home-mobile-light2x.webp'

  const darkIllustrationPathMobile = '/images/modules/search/home-mobile-dark.png'
  const darkIllustrationPathMobile2x = '/images/modules/search/home-mobile-dark2x.png'
  const darkIllustrationPathMobileWebP = '/images/modules/search/home-mobile-dark.webp'
  const darkIllustrationPathMobileWebP2x = '/images/modules/search/home-mobile-dark2x.webp'

  const themeAwareMona = useColorSchemeVar(
    {
      light: lightIllustrationPath,
      light_colorblind: lightIllustrationPath,
      dark: darkIllustrationPath,
      dark_colorblind: darkIllustrationPath,
      dark_dimmed: darkIllustrationPath,
      dark_high_contrast: darkIllustrationPath,
    },
    lightIllustrationPath,
  )

  const themeAwareMonaMobile = useColorSchemeVar(
    {
      light: lightIllustrationPathMobile,
      light_colorblind: lightIllustrationPathMobile,
      dark: darkIllustrationPathMobile,
      dark_colorblind: darkIllustrationPathMobile,
      dark_dimmed: darkIllustrationPathMobile,
      dark_high_contrast: darkIllustrationPathMobile,
    },
    lightIllustrationPathMobile,
  )

  const themeAwareMonaWebP = useColorSchemeVar(
    {
      light: lightIllustrationPathWebP,
      light_colorblind: lightIllustrationPathWebP,
      dark: darkIllustrationPathWebP,
      dark_colorblind: darkIllustrationPathWebP,
      dark_dimmed: darkIllustrationPathWebP,
      dark_high_contrast: darkIllustrationPathWebP,
    },
    lightIllustrationPathWebP,
  )

  const themeAwareMonaMobileWebP = useColorSchemeVar(
    {
      light: lightIllustrationPathMobileWebP,
      light_colorblind: lightIllustrationPathMobileWebP,
      dark: darkIllustrationPathMobileWebP,
      dark_colorblind: darkIllustrationPathMobileWebP,
      dark_dimmed: darkIllustrationPathMobileWebP,
      dark_high_contrast: darkIllustrationPathMobileWebP,
    },
    lightIllustrationPathMobileWebP,
  )

  const themeAwareMona2x = useColorSchemeVar(
    {
      light: lightIllustrationPath2x,
      light_colorblind: lightIllustrationPath2x,
      dark: darkIllustrationPath2x,
      dark_colorblind: darkIllustrationPath2x,
      dark_dimmed: darkIllustrationPath2x,
      dark_high_contrast: darkIllustrationPath2x,
    },
    lightIllustrationPath2x,
  )

  const themeAwareMonaMobile2x = useColorSchemeVar(
    {
      light: lightIllustrationPathMobile2x,
      light_colorblind: lightIllustrationPathMobile2x,
      dark: darkIllustrationPathMobile2x,
      dark_colorblind: darkIllustrationPathMobile2x,
      dark_dimmed: darkIllustrationPathMobile2x,
      dark_high_contrast: darkIllustrationPathMobile2x,
    },
    lightIllustrationPathMobile2x,
  )

  const themeAwareMonaWebP2x = useColorSchemeVar(
    {
      light: lightIllustrationPathWebP2x,
      light_colorblind: lightIllustrationPathWebP2x,
      dark: darkIllustrationPathWebP2x,
      dark_colorblind: darkIllustrationPathWebP2x,
      dark_dimmed: darkIllustrationPathWebP2x,
      dark_high_contrast: darkIllustrationPathWebP2x,
    },
    lightIllustrationPathWebP2x,
  )

  const themeAwareMonaWebMobileP2x = useColorSchemeVar(
    {
      light: lightIllustrationPathMobileWebP2x,
      light_colorblind: lightIllustrationPathMobileWebP2x,
      dark: darkIllustrationPathMobileWebP2x,
      dark_colorblind: darkIllustrationPathMobileWebP2x,
      dark_dimmed: darkIllustrationPathMobileWebP2x,
      dark_high_contrast: darkIllustrationPathMobileWebP2x,
    },
    lightIllustrationPathWebP2x,
  )

  return (
    <Box
      data-hpc
      sx={{
        minHeight: '100vh',
        minWidth: 320,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'row',
          px: 3,
          py: 10,
        }}
      >
        <Box sx={{width: '100%', display: 'flex', alignItems: 'center', flexDirection: 'column'}}>
          <Box sx={{mb: [3, 3, 4]}}>
            <Box sx={{display: ['none', 'none', 'block']}}>
              <LogoGithubIcon size={50} />
            </Box>
            <Box sx={{display: ['block', 'block', 'none']}}>
              <LogoGithubIcon size={30} />
            </Box>
          </Box>
          <Box sx={{px: 2, width: '100%', maxWidth: 600, textAlign: 'center'}}>
            <TextInput
              aria-label="Search GitHub"
              sx={{
                width: '100%',
                height: 42,
              }}
              onKeyPress={handleKeyPress}
              autoFocus
              leadingVisual={() => (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                  <Octicon
                    icon={SearchIcon}
                    sx={{
                      width: [16, 20],
                      height: [16, 20],
                      color: 'fg.muted',
                    }}
                  />
                </Box>
              )}
              trailingVisual={() => (
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                  {value && (
                    <Box
                      onClick={() => setValue('')}
                      sx={{
                        display: 'flex',
                      }}
                    >
                      <Octicon
                        icon={XCircleFillIcon}
                        sx={{
                          color: 'fg.muted',
                          cursor: 'pointer',
                          width: [16, 16],
                          height: [16, 16],

                          '&:hover': {
                            color: 'fg.default',
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
              placeholder="Search GitHub"
              value={value}
              onChange={(e: FormEvent<HTMLInputElement>) => setValue(e.currentTarget.value)}
            />
          </Box>

          <Box sx={{textAlign: 'center', mt: [3, 3, 4]}}>
            <Box sx={{mr: [1, 2], display: 'inline-block'}}>
              <Octicon
                icon={BellIcon}
                sx={{
                  color: 'attention.fg',
                  mr: 1,
                  width: [14, 16],
                  height: [14, 16],
                }}
              />
              <Text sx={{color: 'attention.fg', fontSize: [0, 1]}}>Tip</Text>
            </Box>
            <Text sx={{color: 'fg.muted', fontSize: [0, 1]}}>
              For an <Link href="/search/advanced">advanced search</Link>, use our{' '}
              <Link
                as="button"
                ref={returnFocusRef}
                onClick={() => {
                  setDialogIsOpen(true)
                }}
              >
                prefixes
              </Link>
            </Text>
            <PrefixesDialog
              isOpen={dialogIsOpen}
              returnFocusRef={returnFocusRef}
              rootHelpUrl={payload?.helpUrl as string}
              onDismiss={() => setDialogIsOpen(false)}
            />
          </Box>
          <Box sx={{mt: [6, 8, 8], display: 'flex', justifyContent: 'center'}}>
            <Box sx={{display: ['block', 'block', 'block', 'none', 'none'], maxWidth: [351]}}>
              <picture>
                <source srcSet={`${themeAwareMonaMobileWebP} 1x, ${themeAwareMonaWebMobileP2x} 2x`} type="image/webp" />
                <img
                  src={themeAwareMonaMobile}
                  srcSet={`${themeAwareMonaMobile} 1x, ${themeAwareMonaMobile2x} 2x`}
                  alt="Illustration of Mona looking through binoculars at the a building a supernatural landscape"
                  width="100%"
                />
              </picture>
            </Box>
            <Box sx={{display: ['none', 'none', 'none', 'block', 'block'], maxWidth: [500, 600, 700, 800, 800]}}>
              <picture>
                <source srcSet={`${themeAwareMonaWebP} 1x, ${themeAwareMonaWebP2x} 2x`} type="image/webp" />
                <img
                  src={themeAwareMona}
                  srcSet={`${themeAwareMona} 1x, ${themeAwareMona2x} 2x`}
                  alt="Illustration of Mona looking through binoculars at the a building a supernatural landscape"
                  width="100%"
                />
              </picture>
            </Box>
          </Box>
        </Box>
      </Box>
      <Footer />
    </Box>
  )
}

function Footer() {
  const currentYear = new Date().getFullYear()
  return (
    <>
      <Link aria-label="GitHub" href="/" muted sx={{display: ['block', 'block', 'block', 'none']}}>
        <Octicon
          icon={MarkGithubIcon}
          sx={{
            width: 24,
            height: 24,
            color: 'fg.muted',
          }}
        />
      </Link>
      <Box
        as="footer"
        sx={{
          display: 'flex',
          justifyContent: ['center', 'center', 'center', 'space-between'],
          alignItems: 'center',
          flexWrap: 'wrap',
          flexDirection: ['column', 'column', 'column', 'row'],
          width: '100%',
          px: [5, 5, 5],
          py: 5,
          fontSize: 0,
          color: 'fg.muted',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: [3, 3, 3, 4],
            order: [1, 1, 1, 0],
            p: [2, 2, 2, 0],
            gridTemplateColumns: ['auto auto', 'auto auto', 'auto auto', 'auto auto auto'],
          }}
        >
          <Text sx={{display: ['none', 'none', 'none', 'block']}}>GitHub, Inc. © {currentYear}</Text>
          <Link href="https://docs.github.com/en/github/site-policy/github-terms-of-service" muted>
            Terms
          </Link>
          <Link href="https://docs.github.com/site-policy/privacy-policies/github-privacy-statement" muted>
            Privacy
          </Link>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gap: [3, 3, 3, 4],
            alignItems: 'center',
            gridTemplateColumns: [
              'auto auto auto auto',
              'auto auto auto auto',
              'auto auto auto auto',
              'auto auto auto auto auto',
            ],
          }}
        >
          <Link href="/security" muted>
            Security
          </Link>
          <Link href="https://www.githubstatus.com" muted>
            Status
          </Link>
          <Link href="https://github.blog" muted>
            Blog
          </Link>
          <Link href="/about" muted>
            About
          </Link>
          <Link aria-label="GitHub" href="/" muted sx={{display: ['none', 'none', 'none', 'block']}}>
            <Octicon
              icon={MarkGithubIcon}
              sx={{
                width: 16,
                height: [16, 24],
                color: 'fg.muted',
              }}
            />
          </Link>
        </Box>
      </Box>
    </>
  )
}

function useHideHeader() {
  useEffect(() => {
    const headerDiv: HTMLElement | null =
      document.querySelector('.AppHeader') || document.querySelector('.Header') || document.querySelector('.Header-old')
    if (!headerDiv) {
      return
    }
    headerDiv.hidden = true
    return () => {
      headerDiv.hidden = false
    }
  }, [])
}

try{ Home.displayName ||= 'Home' } catch {}
try{ Footer.displayName ||= 'Footer' } catch {}